import { WORKSPACE_TOOLBAR_ITEMS, CHART_LAYOUT_COMMANDS, CHART_PANE_COMMANDS, CHART_HEADER_COMMANDS, APP_HEADER_COMMANDS } from './workspaceToolbarItems.js'

const MAX_SEQUENCE_LENGTH = 4
const DEFAULT_SEQUENCE_TIMEOUT_MS = 900

const MODIFIER_ORDER = Object.freeze(['Mod', 'Control', 'Meta', 'Alt', 'Shift'])
const MODIFIER_ALIASES = Object.freeze({
  mod: 'Mod',
  cmd: 'Mod',
  command: 'Mod',
  ctrl: 'Control',
  control: 'Control',
  meta: 'Meta',
  alt: 'Alt',
  option: 'Alt',
  shift: 'Shift',
})
const MODIFIER_KEYS = new Set(['alt', 'altgraph', 'control', 'meta', 'shift'])
const KEY_ALIASES = Object.freeze({
  ' ': 'space',
  esc: 'escape',
  return: 'enter',
  spacebar: 'space',
  arrowup: 'arrowup',
  arrowdown: 'arrowdown',
  arrowleft: 'arrowleft',
  arrowright: 'arrowright',
})

export const COMMAND_SCOPES = Object.freeze({
  GLOBAL: 'global',
  MODAL: 'modal',
  EDITOR: 'editor',
  POSITION_WORKSPACE: 'position-workspace',
  STRATEGY_WORKSPACE: 'strategy-workspace',
})

export const COMMAND_PAGES = deepFreeze([
  {
    id: 'all',
    label: 'All Erevis',
    description: 'Every registered Erevis command',
    scope: null,
  },
  {
    id: 'dashboard', label: 'Dashboard', description: 'Dashboard navigation', scope: null, route: '/dashboard',
  },
  {
    id: 'accounts', label: 'Accounts', description: 'Account navigation', scope: null, route: '/accounts/summary',
  },
  {
    id: 'current-positions', label: 'Current Positions', description: 'Current-position navigation', scope: null, route: '/positions/current',
  },
  {
    id: 'position-workspace', label: 'Position Workspace', description: 'Rail selection, focus, and safe action preparation', scope: COMMAND_SCOPES.POSITION_WORKSPACE, route: '/positions/workspace',
  },
  {
    id: 'position-board', label: 'Position Board', description: 'Position-board navigation', scope: null, route: '/positions/board',
  },
  {
    id: 'position-planner', label: 'Position Planner', description: 'Position-planner navigation', scope: null, route: '/positions/planner',
  },
  {
    id: 'trade-log', label: 'Trade Log', description: 'Trade-log navigation', scope: null, route: '/positions/history',
  },
  {
    id: 'strategy-workspace',
    label: 'Strategy Workspace',
    description: 'Strategy-specific navigation and workspace controls',
    scope: COMMAND_SCOPES.STRATEGY_WORKSPACE,
    route: '/strategies',
  },
  {
    id: 'analytics', label: 'Analytics', description: 'Analytics navigation', scope: null, route: '/analytics',
  },
  {
    id: 'settings', label: 'Settings', description: 'Settings navigation', scope: null, route: '/settings',
  },
])

export const COMMAND_SOURCES = Object.freeze({
  KEYBOARD: 'keyboard',
  PALETTE: 'palette',
  MACRO: 'macro',
  UI: 'ui',
})

export const COMMAND_RISKS = Object.freeze({
  READ_ONLY: 'read_only',
  REVERSIBLE_UI: 'reversible_ui',
  WORKFLOW_BARRIER: 'workflow_barrier',
  BROKER_WRITE: 'broker_write',
})

export const MACRO_POLICIES = Object.freeze({
  ALLOWED: 'allowed',
  INTERACTION_BARRIER: 'interaction_barrier',
  FORBIDDEN: 'forbidden',
})

const SCOPE_PRIORITY = Object.freeze({
  [COMMAND_SCOPES.GLOBAL]: 100,
  [COMMAND_SCOPES.POSITION_WORKSPACE]: 200,
  [COMMAND_SCOPES.STRATEGY_WORKSPACE]: 200,
  [COMMAND_SCOPES.EDITOR]: 300,
  [COMMAND_SCOPES.MODAL]: 400,
})

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value
  for (const child of Object.values(value)) deepFreeze(child)
  return Object.freeze(value)
}

function normalizeKey(value) {
  if (typeof value !== 'string') throw new TypeError('A keyboard stroke requires a key')
  const trimmed = value.trim()
  if (!trimmed && value !== ' ') throw new TypeError('A keyboard stroke requires a key')
  const lower = value === ' ' ? ' ' : trimmed.toLowerCase()
  const key = KEY_ALIASES[lower] || lower
  if (!key || MODIFIER_KEYS.has(key)) throw new TypeError('A keyboard stroke requires a non-modifier key')
  return key
}

function normalizeModifier(value) {
  if (typeof value !== 'string') throw new TypeError('Keyboard modifiers must be strings')
  const modifier = MODIFIER_ALIASES[value.trim().toLowerCase()]
  if (!modifier) throw new TypeError(`Unsupported keyboard modifier: ${value}`)
  return modifier
}

function compareModifiers(a, b) {
  return MODIFIER_ORDER.indexOf(a) - MODIFIER_ORDER.indexOf(b)
}

export function normalizePlatform(value = 'mac') {
  const platform = String(value || 'mac').trim().toLowerCase()
  if (platform === 'mac' || platform === 'darwin' || platform === 'macintel') return 'mac'
  if (platform === 'windows' || platform === 'win32' || platform === 'win') return 'windows'
  if (platform === 'linux' || platform === 'linux x86_64') return 'linux'
  if (platform === 'all') return 'all'
  throw new TypeError(`Unsupported keyboard platform: ${value}`)
}

export function normalizeStroke(input) {
  let key
  let modifiers = []

  if (typeof input === 'string') {
    const parts = input.split('+').map((part) => part.trim()).filter(Boolean)
    if (parts.length === 0) throw new TypeError('A keyboard stroke requires a key')
    const possibleKey = parts.at(-1)
    if (parts.length === 1 && MODIFIER_ALIASES[possibleKey.toLowerCase()]) {
      throw new TypeError('A keyboard stroke requires a non-modifier key')
    }
    key = possibleKey
    modifiers = parts.slice(0, -1)
  } else if (input && typeof input === 'object' && !Array.isArray(input)) {
    key = input.key
    modifiers = input.modifiers || []
  } else {
    throw new TypeError('A keyboard stroke must be a string or object')
  }

  if (!Array.isArray(modifiers)) throw new TypeError('Keyboard modifiers must be an array')
  const normalizedModifiers = [...new Set(modifiers.map(normalizeModifier))].sort(compareModifiers)
  return { key: normalizeKey(key), modifiers: normalizedModifiers }
}

export function normalizeSequence(input, { maxLength = MAX_SEQUENCE_LENGTH } = {}) {
  const strokes = typeof input === 'string'
    ? input.trim().split(/\s+/).filter(Boolean)
    : input
  if (!Array.isArray(strokes) || strokes.length === 0) {
    throw new TypeError('A keyboard sequence requires at least one stroke')
  }
  if (strokes.length > maxLength) {
    throw new RangeError(`Keyboard sequences are limited to ${maxLength} strokes`)
  }
  return strokes.map(normalizeStroke)
}

export function strokeFromKeyboardEvent(event, platform = 'mac') {
  if (!event || typeof event !== 'object') throw new TypeError('A keyboard event is required')
  const normalizedPlatform = normalizePlatform(platform)
  if (normalizedPlatform === 'all') throw new TypeError('Keyboard events require a concrete platform')

  const modifiers = []
  if (normalizedPlatform === 'mac') {
    if (event.metaKey) modifiers.push('Mod')
    if (event.ctrlKey) modifiers.push('Control')
  } else {
    if (event.ctrlKey) modifiers.push('Mod')
    if (event.metaKey) modifiers.push('Meta')
  }
  if (event.altKey) modifiers.push('Alt')
  if (event.shiftKey) modifiers.push('Shift')
  const physicalLetter = event.altKey && /^Key[A-Z]$/.test(String(event.code || ''))
    ? event.code.slice(3)
    : null
  return normalizeStroke({ key: physicalLetter || event.key, modifiers })
}

function displayKey(key) {
  const labels = {
    arrowup: '↑',
    arrowdown: '↓',
    arrowleft: '←',
    arrowright: '→',
    escape: 'Esc',
    enter: 'Enter',
    space: 'Space',
    tab: 'Tab',
    backspace: 'Backspace',
    delete: 'Delete',
  }
  return labels[key] || (key.length === 1 ? key.toUpperCase() : key)
}

export function formatStroke(input, platform = 'mac') {
  const stroke = normalizeStroke(input)
  const normalizedPlatform = normalizePlatform(platform)
  const platformForDisplay = normalizedPlatform === 'all' ? 'mac' : normalizedPlatform
  if (platformForDisplay === 'mac') {
    const glyphs = {
      Mod: '⌘',
      Control: '⌃',
      Meta: '⌘',
      Alt: '⌥',
      Shift: '⇧',
    }
    return `${stroke.modifiers.map((modifier) => glyphs[modifier]).join('')}${displayKey(stroke.key)}`
  }
  const labels = {
    Mod: 'Ctrl',
    Control: 'Ctrl',
    Meta: 'Meta',
    Alt: 'Alt',
    Shift: 'Shift',
  }
  return [...stroke.modifiers.map((modifier) => labels[modifier]), displayKey(stroke.key)].join('+')
}

export function formatSequence(input, platform = 'mac') {
  return normalizeSequence(input).map((stroke) => formatStroke(stroke, platform)).join(' ')
}

function getAttribute(target, name) {
  try {
    return typeof target?.getAttribute === 'function' ? target.getAttribute(name) : null
  } catch {
    return null
  }
}

export function isEditableTarget(target) {
  let current = target
  let depth = 0
  while (current && depth < 12) {
    const tag = String(current.tagName || '').toUpperCase()
    const role = String(getAttribute(current, 'role') || '').toLowerCase()
    if (
      tag === 'INPUT' ||
      tag === 'TEXTAREA' ||
      tag === 'SELECT' ||
      current.isContentEditable === true ||
      (getAttribute(current, 'contenteditable') != null && getAttribute(current, 'contenteditable') !== 'false') ||
      role === 'textbox' ||
      role === 'searchbox' ||
      role === 'combobox' ||
      getAttribute(current, 'data-command-shield') === 'true'
    ) return true
    current = current.parentElement || null
    depth += 1
  }
  return false
}

export function shouldShieldKeyboardEvent(event, { allowRepeat = false } = {}) {
  if (!event || typeof event !== 'object') return true
  const key = String(event.key || '').toLowerCase()
  return event.defaultPrevented === true ||
    event.isComposing === true ||
    event.keyCode === 229 ||
    (event.repeat === true && !allowRepeat) ||
    !key ||
    MODIFIER_KEYS.has(key) ||
    isEditableTarget(event.target)
}

const ALL_INTERACTION_SOURCES = Object.freeze([
  COMMAND_SOURCES.KEYBOARD,
  COMMAND_SOURCES.PALETTE,
  COMMAND_SOURCES.MACRO,
  COMMAND_SOURCES.UI,
])
const DIRECT_SOURCES = Object.freeze([
  COMMAND_SOURCES.KEYBOARD,
  COMMAND_SOURCES.PALETTE,
  COMMAND_SOURCES.UI,
])

function createCommand({
  id,
  title,
  category,
  scope = COMMAND_SCOPES.GLOBAL,
  risk = COMMAND_RISKS.READ_ONLY,
  allowedSources = ALL_INTERACTION_SOURCES,
  macroPolicy = MACRO_POLICIES.ALLOWED,
  pageId = 'global',
  repeatable = false,
  route,
}) {
  return deepFreeze({
    id,
    title,
    category,
    scope,
    risk,
    allowedSources: [...allowedSources],
    macroPolicy,
    pageId,
    repeatable,
    ...(route ? { route } : {}),
  })
}

export const COMMAND_DEFINITIONS = deepFreeze([
  createCommand({
    id: 'position.chart.price_alert.create', title: 'Create price alert at crosshair',
    category: 'Position Workspace', pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE, risk: COMMAND_RISKS.REVERSIBLE_UI,
    macroPolicy: MACRO_POLICIES.INTERACTION_BARRIER,
  }),
  createCommand({
    id: 'position.chart.execution_markers.toggle', title: 'Toggle execution markers on selected chart',
    category: 'Position Workspace', pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE, risk: COMMAND_RISKS.REVERSIBLE_UI,
  }),
  createCommand({
    id: 'position.chart.restore', title: 'Exit expanded chart', category: 'Position Workspace',
    pageId: 'position-workspace', scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.REVERSIBLE_UI,
  }),
  ...[
    ['settings.open', 'Open chart settings'],
    ['zoom_in', 'Zoom in on selected chart'],
    ['zoom_out', 'Zoom out on selected chart'],
    ['scroll_left', 'Scroll selected chart left'],
    ['scroll_right', 'Scroll selected chart right'],
  ].map(([name, title]) => createCommand({
    repeatable: name !== 'settings.open',
    id: `position.chart.${name}`, title, category: 'Position Workspace',
    pageId: 'position-workspace', scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.REVERSIBLE_UI,
  })),
  ...[...WORKSPACE_TOOLBAR_ITEMS, ...CHART_LAYOUT_COMMANDS, ...CHART_PANE_COMMANDS, ...CHART_HEADER_COMMANDS].map(({ id, title }) => createCommand({
    id, title, category: 'Position Workspace', pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE, risk: COMMAND_RISKS.REVERSIBLE_UI,
  })),
  ...APP_HEADER_COMMANDS.map(({ id, title }) => createCommand({
    id, title, category: 'App controls', pageId: 'global',
    risk: COMMAND_RISKS.REVERSIBLE_UI,
  })),
  createCommand({
    id: 'command.palette.open',
    title: 'Open command palette',
    category: 'Commands',
    allowedSources: DIRECT_SOURCES,
    macroPolicy: MACRO_POLICIES.FORBIDDEN,
  }),
  createCommand({
    id: 'command.help.open',
    title: 'Open keyboard shortcuts',
    category: 'Commands',
    allowedSources: DIRECT_SOURCES,
    macroPolicy: MACRO_POLICIES.FORBIDDEN,
  }),
  createCommand({ id: 'nav.dashboard', title: 'Go to Dashboard', category: 'Navigation', pageId: 'dashboard', route: '/dashboard' }),
  createCommand({ id: 'nav.accounts', title: 'Go to Accounts', category: 'Navigation', pageId: 'accounts', route: '/accounts/summary' }),
  createCommand({ id: 'nav.positions', title: 'Go to Current Positions', category: 'Navigation', pageId: 'current-positions', route: '/positions/current' }),
  createCommand({ id: 'nav.position_workspace', title: 'Go to Position Workspace', category: 'Navigation', pageId: 'position-workspace', route: '/positions/workspace' }),
  createCommand({ id: 'nav.position_board', title: 'Go to Position Board', category: 'Navigation', pageId: 'position-board', route: '/positions/board' }),
  createCommand({ id: 'nav.position_planner', title: 'Go to Position Planner', category: 'Navigation', pageId: 'position-planner', route: '/positions/planner' }),
  createCommand({ id: 'nav.trade_log', title: 'Go to Trade Log', category: 'Navigation', pageId: 'trade-log', route: '/positions/history' }),
  createCommand({ id: 'nav.strategies', title: 'Go to Strategies', category: 'Navigation', pageId: 'strategy-workspace', route: '/strategies' }),
  createCommand({ id: 'nav.analytics', title: 'Go to Analytics', category: 'Navigation', pageId: 'analytics', route: '/analytics' }),
  createCommand({ id: 'nav.settings', title: 'Go to Settings', category: 'Navigation', pageId: 'settings', route: '/settings' }),
  createCommand({
    id: 'position.asset.search',
    title: 'Search assets',
    description: 'Open asset search in the Position Workspace',
    category: 'Position Workspace',
    pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.REVERSIBLE_UI,
  }),
  createCommand({
    id: 'position.asset.previous',
    title: 'Open previous asset in list',
    category: 'Position Workspace',
    pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.REVERSIBLE_UI,
  }),
  createCommand({
    id: 'position.asset.next',
    title: 'Open next asset in list',
    category: 'Position Workspace',
    pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.REVERSIBLE_UI,
  }),
  createCommand({
    id: 'position.left_column.focus',
    title: 'Focus left column',
    category: 'Position Workspace',
    pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.REVERSIBLE_UI,
  }),
  createCommand({
    id: 'position.right_column.focus',
    title: 'Focus right column',
    category: 'Position Workspace',
    pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.REVERSIBLE_UI,
  }),
  createCommand({
    id: 'position.ideas.add_current_asset',
    title: 'Add current asset to Ideas List',
    category: 'Position Workspace',
    pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.REVERSIBLE_UI,
    allowedSources: DIRECT_SOURCES,
    macroPolicy: MACRO_POLICIES.FORBIDDEN,
  }),
  createCommand({
    id: 'position.watchlist.add_current_asset',
    title: 'Add current asset to selected watchlist',
    category: 'Position Workspace',
    pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.REVERSIBLE_UI,
    allowedSources: DIRECT_SOURCES,
    macroPolicy: MACRO_POLICIES.FORBIDDEN,
  }),
  createCommand({
    id: 'position.rail.previous',
    title: 'Highlight previous position or idea',
    category: 'Position Workspace',
    pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.REVERSIBLE_UI,
  }),
  createCommand({
    id: 'position.rail.next',
    title: 'Highlight next position or idea',
    category: 'Position Workspace',
    pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.REVERSIBLE_UI,
  }),
  createCommand({
    id: 'position.rail.open',
    title: 'Open highlighted position or idea',
    category: 'Position Workspace',
    pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE,
  }),
  createCommand({
    id: 'position.actions.focus',
    title: 'Focus Quick Actions',
    category: 'Position Workspace',
    pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.REVERSIBLE_UI,
  }),
  createCommand({
    id: 'position.move_stop.focus',
    title: 'Focus Move Stop quick action',
    category: 'Position Workspace',
    pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.REVERSIBLE_UI,
  }),
  createCommand({
    id: 'position.move_stop.start',
    title: 'Move Stop',
    category: 'Position Actions',
    pageId: 'position-workspace',
    scope: COMMAND_SCOPES.POSITION_WORKSPACE,
    risk: COMMAND_RISKS.WORKFLOW_BARRIER,
    macroPolicy: MACRO_POLICIES.INTERACTION_BARRIER,
  }),
])

export const COMMAND_BY_ID = deepFreeze(Object.fromEntries(
  COMMAND_DEFINITIONS.map((definition) => [definition.id, definition])
))

function createDefaultBinding(id, commandId, sequence, platform = 'all') {
  const command = COMMAND_BY_ID[commandId]
  if (!command) throw new Error(`Default binding references unknown command: ${commandId}`)
  return deepFreeze({
    id,
    commandId,
    sequence: normalizeSequence(sequence),
    platform: normalizePlatform(platform),
    enabled: true,
    source: 'default',
    scope: command.scope,
    risk: command.risk,
  })
}

export const DEFAULT_BINDINGS = deepFreeze([
  ...[['pen','d d'],['select','a a'],['eraser','e e'],['settings','d s'],['selectAll','s a d'],['clearAll','c d d']]
    .map(([tool,sequence]) => createDefaultBinding(`default.position.drawing.${tool}`, `position.drawing.${tool}`, sequence)),
  createDefaultBinding('default.position.chart.price_alert.create', 'position.chart.price_alert.create', 'Alt+a'),
  createDefaultBinding('default.position.alerts.open', 'position.alerts.open', 's a p'),
  ...[['zoom_in', 'ArrowUp'], ['zoom_out', 'ArrowDown'], ['scroll_left', 'ArrowLeft'], ['scroll_right', 'ArrowRight']]
    .map(([name, key]) => createDefaultBinding(`default.position.chart.${name}`, `position.chart.${name}`, `Mod+${key}`)),
  createDefaultBinding('default.position.drawing.undo', 'position.drawing.undo', 'Mod+z'),
  createDefaultBinding('default.position.drawing.delete', 'position.drawing.delete', 'Mod+x'),
  createDefaultBinding('default.position.drawing.redo', 'position.drawing.redo', 'Mod+Shift+z'),
  createDefaultBinding('default.position.ideas.add_current_asset', 'position.ideas.add_current_asset', 'a w i'),
  createDefaultBinding('default.command.palette', 'command.palette.open', 'Mod+k'),
  createDefaultBinding('default.command.help', 'command.help.open', 'Shift+?'),
  createDefaultBinding('default.nav.dashboard', 'nav.dashboard', 'g d'),
  createDefaultBinding('default.nav.accounts', 'nav.accounts', 'g a'),
  createDefaultBinding('default.nav.positions', 'nav.positions', 'g p'),
  createDefaultBinding('default.nav.strategies', 'nav.strategies', 'g s'),
  createDefaultBinding('default.nav.analytics', 'nav.analytics', 'g y'),
  createDefaultBinding('default.nav.settings', 'nav.settings', 'g t'),
  createDefaultBinding('default.position.asset.search', 'position.asset.search', 'Control+Space', 'mac'),
  createDefaultBinding('default.position.asset.search.windows', 'position.asset.search', 'Mod+Space', 'windows'),
  createDefaultBinding('default.position.asset.search.linux', 'position.asset.search', 'Mod+Space', 'linux'),
  createDefaultBinding('default.position.asset.previous', 'position.asset.previous', 'Shift+Space'),
  createDefaultBinding('default.position.asset.next', 'position.asset.next', 'Space'),
  createDefaultBinding('default.position.left_column.focus', 'position.left_column.focus', 'l c'),
  createDefaultBinding('default.position.right_column.focus', 'position.right_column.focus', 'r c'),
  createDefaultBinding('default.position.watchlist.add_current_asset', 'position.watchlist.add_current_asset', 'Alt+w', 'mac'),
  createDefaultBinding('default.position.move_stop', 'position.move_stop.start', 'a m s'),
])

function createReservedShortcut(id, sequence, platforms = ['mac', 'windows', 'linux']) {
  return deepFreeze({ id, sequence: normalizeSequence(sequence), platforms })
}

export const RESERVED_BROWSER_SHORTCUTS = deepFreeze([
  createReservedShortcut('browser.location', 'Mod+l'),
  createReservedShortcut('browser.reload', 'Mod+r'),
  createReservedShortcut('browser.close_tab', 'Mod+w'),
  createReservedShortcut('browser.new_tab', 'Mod+t'),
  createReservedShortcut('browser.new_window', 'Mod+n'),
  createReservedShortcut('browser.private_window', 'Mod+Shift+n'),
  createReservedShortcut('browser.reopen_tab', 'Mod+Shift+t'),
  createReservedShortcut('browser.close_window', 'Mod+Shift+w'),
  createReservedShortcut('browser.quit', 'Mod+q', ['mac']),
  createReservedShortcut('browser.save', 'Mod+s'),
  createReservedShortcut('browser.open', 'Mod+o'),
  createReservedShortcut('browser.print', 'Mod+p'),
  createReservedShortcut('browser.find', 'Mod+f'),
  createReservedShortcut('browser.settings', 'Mod+,'),
  ...Array.from({ length: 9 }, (_, index) => (
    createReservedShortcut(`browser.tab_${index + 1}`, `Mod+${index + 1}`)
  )),
  createReservedShortcut('browser.back', 'Alt+ArrowLeft'),
  createReservedShortcut('browser.forward', 'Alt+ArrowRight'),
])

function sequenceKey(sequence) {
  return normalizeSequence(sequence)
    .map((stroke) => `${stroke.modifiers.join('+')}|${stroke.key}`)
    .join(' ')
}

function platformMatches(bindingPlatform, activePlatform) {
  const binding = normalizePlatform(bindingPlatform || 'all')
  const active = normalizePlatform(activePlatform)
  return binding === 'all' || active === 'all' || binding === active
}

export function scopesOverlap(left, right) {
  if (!left || !right) return false
  return left === right || left === COMMAND_SCOPES.GLOBAL || right === COMMAND_SCOPES.GLOBAL
}

function scopeIsActive(scope, activeScopes) {
  if (scope === COMMAND_SCOPES.GLOBAL) return true
  return Array.isArray(activeScopes) && activeScopes.includes(scope)
}

export function canInvokeCommand(command, {
  source = COMMAND_SOURCES.KEYBOARD,
  activeScopes,
} = {}) {
  if (!command || typeof command !== 'object') {
    return { allowed: false, barrier: false, reason: 'unknown_command' }
  }
  if (source === COMMAND_SOURCES.MACRO && command.risk === COMMAND_RISKS.BROKER_WRITE) {
    return { allowed: false, barrier: false, reason: 'broker_write_forbidden_from_macro' }
  }
  if (!Array.isArray(command.allowedSources) || !command.allowedSources.includes(source)) {
    return { allowed: false, barrier: false, reason: 'source_not_allowed' }
  }
  if (source === COMMAND_SOURCES.MACRO && command.macroPolicy === MACRO_POLICIES.FORBIDDEN) {
    return { allowed: false, barrier: false, reason: 'macro_forbidden' }
  }
  if (activeScopes && !scopeIsActive(command.scope, activeScopes)) {
    return { allowed: false, barrier: false, reason: 'scope_inactive' }
  }
  return {
    allowed: true,
    barrier: source === COMMAND_SOURCES.MACRO && command.macroPolicy === MACRO_POLICIES.INTERACTION_BARRIER,
    reason: null,
  }
}

export function isReservedBrowserSequence(sequence, platform = 'mac') {
  const normalizedPlatform = normalizePlatform(platform)
  const key = sequenceKey(sequence)
  return RESERVED_BROWSER_SHORTCUTS.some((reserved) => (
    (normalizedPlatform === 'all' || reserved.platforms.includes(normalizedPlatform)) &&
    sequenceKey(reserved.sequence) === key
  ))
}

function isSequencePrefix(left, right) {
  if (left.length >= right.length) return false
  return left.every((stroke, index) => sequenceKey([stroke]) === sequenceKey([right[index]]))
}

export function detectBindingConflicts(bindings, { platform = 'mac' } = {}) {
  const activePlatform = normalizePlatform(platform)
  const candidates = (Array.isArray(bindings) ? bindings : [])
    .filter((binding) => binding?.enabled !== false && platformMatches(binding?.platform, activePlatform))
    .map((binding) => {
      try {
        return { ...binding, sequence: normalizeSequence(binding.sequence) }
      } catch {
        return null
      }
    })
    .filter(Boolean)
    .sort((a, b) => String(a.id).localeCompare(String(b.id)))

  const conflicts = []
  for (let i = 0; i < candidates.length; i += 1) {
    for (let j = i + 1; j < candidates.length; j += 1) {
      const left = candidates[i]
      const right = candidates[j]
      if (!scopesOverlap(left.scope, right.scope)) continue
      const leftKey = sequenceKey(left.sequence)
      const rightKey = sequenceKey(right.sequence)
      let type = null
      if (leftKey === rightKey) type = 'exact'
      else if (isSequencePrefix(left.sequence, right.sequence) || isSequencePrefix(right.sequence, left.sequence)) type = 'prefix'
      if (!type) continue
      conflicts.push(deepFreeze({
        type,
        bindingIds: [String(left.id), String(right.id)].sort(),
        commandIds: [left.commandId, right.commandId],
      }))
    }
  }

  conflicts.sort((a, b) => {
    const first = a.bindingIds[0].localeCompare(b.bindingIds[0])
    if (first) return first
    if (a.type !== b.type) return a.type === 'exact' ? -1 : 1
    return a.bindingIds[1].localeCompare(b.bindingIds[1])
  })
  return deepFreeze(conflicts)
}

export function buildEffectiveBindings({
  commands = COMMAND_BY_ID,
  defaultBindings = DEFAULT_BINDINGS,
  userBindings = [],
  disabledDefaultBindingIds = [],
  platform = 'mac',
} = {}) {
  const activePlatform = normalizePlatform(platform)
  const disabledDefaults = new Set(disabledDefaultBindingIds || [])
  const effective = []
  const invalid = []
  const seenIds = new Set()

  for (const binding of Array.isArray(defaultBindings) ? defaultBindings : []) {
    seenIds.add(binding.id)
    if (disabledDefaults.has(binding.id) || binding.enabled === false) continue
    if (!platformMatches(binding.platform, activePlatform)) continue
    const command = commands[binding.commandId]
    if (!command) {
      invalid.push({ id: binding.id, reason: 'unknown_command' })
      continue
    }
    effective.push(deepFreeze({
      ...binding,
      sequence: normalizeSequence(binding.sequence),
      scope: command.scope,
      risk: command.risk,
      source: 'default',
    }))
  }

  for (const raw of Array.isArray(userBindings) ? userBindings : []) {
    const id = typeof raw?.id === 'string' && raw.id.trim() ? raw.id.trim() : null
    if (!id) {
      invalid.push({ id: null, reason: 'invalid_binding_id' })
      continue
    }
    if (seenIds.has(id)) {
      invalid.push({ id, reason: 'duplicate_binding_id' })
      continue
    }
    seenIds.add(id)

    const command = commands[raw.commandId]
    if (!command) {
      invalid.push({ id, reason: 'unknown_command' })
      continue
    }
    let sequence
    try {
      sequence = normalizeSequence(raw.sequence)
    } catch {
      invalid.push({ id, reason: 'invalid_sequence' })
      continue
    }
    let bindingPlatform
    try {
      bindingPlatform = raw.platform == null ? 'all' : normalizePlatform(raw.platform)
    } catch {
      invalid.push({ id, reason: 'invalid_platform' })
      continue
    }
    if (isReservedBrowserSequence(sequence, activePlatform)) {
      invalid.push({ id, reason: 'reserved_browser_shortcut' })
      continue
    }
    if (raw.enabled === false || !platformMatches(bindingPlatform, activePlatform)) continue

    effective.push(deepFreeze({
      id,
      commandId: command.id,
      sequence,
      platform: bindingPlatform,
      enabled: true,
      source: 'user',
      scope: command.scope,
      risk: command.risk,
    }))
  }

  effective.sort((a, b) => String(a.id).localeCompare(String(b.id)))
  const bindings = deepFreeze(effective)
  return deepFreeze({
    bindings,
    invalid,
    conflicts: detectBindingConflicts(bindings, { platform: activePlatform }),
  })
}

function resolveCommands(commands) {
  if (Array.isArray(commands)) return Object.fromEntries(commands.map((entry) => [entry.id, entry]))
  return commands || COMMAND_BY_ID
}

function startsWithSequence(sequence, prefix) {
  if (prefix.length > sequence.length) return false
  return prefix.every((stroke, index) => sequenceKey([stroke]) === sequenceKey([sequence[index]]))
}

function commandScopePriority(command) {
  return SCOPE_PRIORITY[command?.scope] || 0
}

export function createSequenceMatcher(bindings, {
  commands = COMMAND_BY_ID,
  platform = 'mac',
  timeoutMs = DEFAULT_SEQUENCE_TIMEOUT_MS,
  now = () => Date.now(),
} = {}) {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) throw new RangeError('Sequence timeout must be positive')
  const commandMap = resolveCommands(commands)
  const activePlatform = normalizePlatform(platform)
  const normalizedBindings = (Array.isArray(bindings) ? bindings : [])
    .map((binding) => {
      try {
        return deepFreeze({ ...binding, sequence: normalizeSequence(binding.sequence) })
      } catch {
        return null
      }
    })
    .filter(Boolean)
    .sort((a, b) => String(a.id).localeCompare(String(b.id)))

  let pendingSequence = []
  let expiresAt = null
  let heldNavigationPrefix = null

  function expirePending() {
    pendingSequence = []
    expiresAt = null
  }

  function reset() {
    expirePending()
    heldNavigationPrefix = null
  }

  function releaseEvent(event) {
    try {
      if (normalizeKey(event.key) === heldNavigationPrefix?.key) heldNavigationPrefix = null
    } catch { /* Modifier releases do not own a navigation prefix. */ }
  }

  function pending() {
    return pendingSequence.map((stroke) => ({ ...stroke, modifiers: [...stroke.modifiers] }))
  }

  function activeBindings(context) {
    const source = context.source || COMMAND_SOURCES.KEYBOARD
    const activeScopes = context.activeScopes || [COMMAND_SCOPES.GLOBAL]
    return normalizedBindings.filter((binding) => {
      if (binding.enabled === false || !platformMatches(binding.platform, activePlatform)) return false
      const command = commandMap[binding.commandId]
      if (!command) return false
      return canInvokeCommand(command, { source, activeScopes }).allowed
    })
  }

  function resultNone() {
    return { status: 'none', preventDefault: false, pendingSequence: [] }
  }

  function push(input, context = {}) {
    const currentTime = now()
    if (expiresAt != null && currentTime >= expiresAt) expirePending()

    let stroke
    try {
      stroke = normalizeStroke(input)
    } catch {
      expirePending()
      return resultNone()
    }

    const eligible = activeBindings(context)
    let attempt = [...pendingSequence, stroke]
    let candidates = eligible.filter((binding) => startsWithSequence(binding.sequence, attempt))
    if (candidates.length === 0 && pendingSequence.length > 0) {
      attempt = [stroke]
      candidates = eligible.filter((binding) => startsWithSequence(binding.sequence, attempt))
    }
    if (candidates.length === 0) {
      expirePending()
      return resultNone()
    }

    const exact = candidates.filter((binding) => binding.sequence.length === attempt.length)
    const longer = candidates.filter((binding) => binding.sequence.length > attempt.length)
    if (exact.length > 0) {
      if (longer.length > 0) {
        expirePending()
        return {
          status: 'ambiguous',
          preventDefault: true,
          pendingSequence: [],
          candidateBindingIds: candidates.map((binding) => binding.id),
        }
      }

      const ranked = exact
        .map((binding) => ({ binding, command: commandMap[binding.commandId] }))
        .sort((left, right) => (
          commandScopePriority(right.command) - commandScopePriority(left.command) ||
          String(left.binding.id).localeCompare(String(right.binding.id))
        ))
      const topPriority = commandScopePriority(ranked[0].command)
      const top = ranked.filter((entry) => commandScopePriority(entry.command) === topPriority)
      if (top.length !== 1) {
        expirePending()
        return {
          status: 'ambiguous',
          preventDefault: true,
          pendingSequence: [],
          candidateBindingIds: top.map((entry) => entry.binding.id),
        }
      }
      expirePending()
      return {
        status: 'matched',
        preventDefault: true,
        pendingSequence: [],
        binding: top[0].binding,
        command: top[0].command,
      }
    }

    pendingSequence = attempt
    expiresAt = currentTime + timeoutMs
    return {
      status: 'pending',
      preventDefault: true,
      pendingSequence: pending(),
      candidateBindingIds: candidates.map((binding) => binding.id),
      expiresAt,
    }
  }

  function pushEvent(event, context = {}) {
    if (shouldShieldKeyboardEvent(event, { allowRepeat: true })) {
      reset()
      return resultNone()
    }
    try {
      const stroke = strokeFromKeyboardEvent(event, activePlatform)
      const navigationBindings = activeBindings(context).filter(binding =>
        ['position.asset.previous', 'position.asset.next'].includes(binding.commandId)
        && binding.sequence.length === 2
        && binding.sequence[0].modifiers.length === 0
        && binding.sequence[0].key.length === 1)
      const heldCompletion = heldNavigationPrefix && navigationBindings.some(binding =>
        sequenceKey([binding.sequence[0]]) === sequenceKey([heldNavigationPrefix])
        && sequenceKey([binding.sequence[1]]) === sequenceKey([stroke]))

      // A held prefix is a modifier only for configured list navigation. Each
      // terminal press is deliberate; OS repeats must not advance the list.
      if (event.repeat && (heldCompletion || stroke.key === heldNavigationPrefix?.key)) return resultNone()
      if (!event.repeat) {
        if (heldCompletion) {
          pendingSequence = [heldNavigationPrefix]
          expiresAt = now() + timeoutMs
        } else {
          heldNavigationPrefix = navigationBindings.some(binding =>
            sequenceKey([binding.sequence[0]]) === sequenceKey([stroke])) ? stroke : null
        }
      }
      // Keep the existing repeat policy for single-stroke chart navigation.
      if (event.repeat && pendingSequence.length) return resultNone()
      const result = push(stroke, context)
      if (!event.repeat) return result
      if (result.status === 'matched' && result.binding.sequence.length === 1 && result.command.repeatable === true) return result
      reset()
      return resultNone()
    } catch {
      reset()
      return resultNone()
    }
  }

  return Object.freeze({ push, pushEvent, releaseEvent, pending, expirePending, reset })
}
